// @flow
import React, {Component} from 'react';
import {connect} from 'react-redux';
import styled from 'styled-components';
import createDebugger from 'debug';
const debug = createDebugger('app:components:SlackCommunityChatModal'); // eslint-disable-line no-unused-vars
import { ThemeType } from 'trustar-2.0/types';
import {Modal, NuModalTitle as ModalTitle} from 'trustar/components';
import {requestToJoinCommunityChat, resetChatModal} from 'modules/chat/actions';
import * as ChatGetters from 'modules/chat/getters';
import {Spinner} from 'trustar-2.0/components';
import {USER_STATUSES_FOR_SLACK_COMMUNITY} from 'modules/chat/constants';
import {TRUSTAR_SUPPORT_PAGE} from 'modules/app/constants';
import {Button} from 'trustar-2.0/components/Button';
import ButtonGroup from 'trustar-2.0/components/Button/ButtonGroup';
import {Icon, IconLookup} from 'trustar-2.0/components';
import {List} from 'immutable';

// Style for Main Components (things you may export)
const StyledSlackCommunityChatModal = styled(Modal)`
    width: 41rem;
    padding: 0rem;
    border-radius: 1px;
    color: ${({ theme }) => theme.ThemeColors.TEXT.hex};
    background-color: ${({theme}) => theme.ThemeColors.SURFACE.hex};

    .modal-title {
      color: ${({ theme }) => theme.ThemeColors.TEXT.hex};
      background: ${({theme}) => theme.ThemeColors.LIGHT_GRAY_SURFACE.hex};
    }
    .loading-spinner {
      padding: 1rem;
    }
    
    h5 {
      padding: 1rem;
      margin: 0;
    }
`;

const initialValues = {
  isOpen: false,
};

// Bindings
type SlackCommunityChatModalProps = {
  theme?: ThemeType,
  isOpen: boolean,
  joinRequestInProgress: boolean,
  onExitModal: Function,
  resetChatModal: Function,
  requestToJoinCommunityChat: Function,
  slackInvitationStatus: Object,
};

class SlackCommunityChatModal extends Component {
  props: SlackCommunityChatModalProps;
  static defaultProps = initialValues;

//  ----Template----

  render() {
    const {isOpen, slackInvitationStatus, joinRequestInProgress} = this.props;

    return (
        <StyledSlackCommunityChatModal isOpen={isOpen} onToggleModal={this.onToggleModal}>
          <ModalTitle className={'modal-title'}>TruSTAR Community Chat</ModalTitle>
          <div>
            <h5> {this.showMessageForStatus(slackInvitationStatus)} </h5>
          </div>
          {joinRequestInProgress ? <Spinner className={'loading-spinner'} inFlexContainer={true} />
            :
            <ModalBottomButtonBar slackInvitationStatus={slackInvitationStatus} onExitModal={this.onToggleModal} goToSlackCommunity={this.goToSlackCommunity} />
          }
        </StyledSlackCommunityChatModal>
    );
  }

  // ----Methods----
  showMessageForStatus = (statusObject: Object) => {
    const {status, workspaceUrl} = statusObject;

    if (status === USER_STATUSES_FOR_SLACK_COMMUNITY.USER_ALREADY_EXISTS) {
      return statusMessagesToUser.USER_ALREADY_EXISTS(workspaceUrl);
    }

    return statusMessagesToUser[status] || statusMessagesToUser.DEFAULT;
  };

  onToggleModal = () => {
    const {resetChatModal, onExitModal} = this.props;
      resetChatModal();
      onExitModal();
  };

  goToSlackCommunity = (resendEmail?: boolean) => {
    const {requestToJoinCommunityChat} = this.props;
    // TODO Validation? Reduce useless calls?

    requestToJoinCommunityChat(resendEmail);
  };
}

// Helper Components and Utils
const StyledModalBottomButtonBar = styled(ButtonGroup)`
  width: 100%;
  padding: 0 1rem 1rem 1rem;
`;

const StyledSupportLinkBox = styled.div`
  flex-grow: 1;
`;

const SupportLinkBox = () => {
  return (
    <StyledSupportLinkBox>
      <a href={TRUSTAR_SUPPORT_PAGE} target={'_blank'}> Contact Support </a>
    </StyledSupportLinkBox>
  )
}

// These statuses will also provide the user with a link to contact support
const supportWorthyStatuses = List(['USER_DISABLED', 'USER_ALREADY_INVITED', 'ERROR']);

const ModalBottomButtonBar = (props: Object) => {
  const {onExitModal, goToSlackCommunity, slackInvitationStatus} = props;
  const allowResend = slackInvitationStatus.status === USER_STATUSES_FOR_SLACK_COMMUNITY.USER_ALREADY_INVITED && !slackInvitationStatus.resendAlreadyClicked;
  const allowFiringARequest = allowResend || !statusMessagesToUser[slackInvitationStatus.status];

  // Hmm maybe refactor statuses
  const showSupportLink = supportWorthyStatuses.includes(slackInvitationStatus.status);

    return (
      <StyledModalBottomButtonBar block alignRight>
        {showSupportLink && <SupportLinkBox />}
        <Button buttonType={'secondary'} fill='none' onClick={onExitModal}> {allowFiringARequest ? 'Cancel' : 'Close'} </Button>
        {allowFiringARequest && <Button onClick={() => goToSlackCommunity(allowResend)}> {allowResend ? 'Resend E-mail' : 'Go to Community Chat'} </Button>}
      </StyledModalBottomButtonBar>
    )
};

const AdditionalInformation = styled.span`
  display: block;
  font-size: 1.2rem;
`;

const statusMessagesToUser = {
  INVITE_SENT: 'An invite has been sent to your e-mail address, please check for it and click on the link provided.',
  // eslint-disable-next-line react/display-name
  USER_ALREADY_EXISTS: (workspaceUrl) => <span> You already signed up to slack workspace, please go to <a target={'_blank'} href={workspaceUrl}>{workspaceUrl}</a>.</span>,
  USER_ALREADY_INVITED: 'An invite has already been sent to your e-mail address, please check your spam folders and if the problem persists contact support.',
  USER_DISABLED: 'This user has been disabled from the workspace and cannot join.',
  ERROR: 'An error occurred while sending the request, please try again or contact support.',
  DEFAULT: <span>Go to TruSTAR Community Slack Channel? <AdditionalInformation><Icon icon={IconLookup.NOTIFICATION_CIRCLE} /> First time users will receive an invite to their e-mail address </AdditionalInformation></span>
};

const mapState = state => ({
  joinRequestInProgress: ChatGetters.joinRequestInProgress(state),
  slackInvitationStatus: ChatGetters.slackInvitationStatus(state),
});

const dispatchProps = {
  requestToJoinCommunityChat,
  resetChatModal,
};

const connectedModal = connect(mapState, dispatchProps)(SlackCommunityChatModal);

export {SlackCommunityChatModal as PureSlackCommunityChatModal, connectedModal as SlackCommunityChatModal};



// WEBPACK FOOTER //
// ./src/app/components/modals/SlackCommunityChatModal.js